// src/components/games/crossroads/environmentDecks.ts
// Environment deck definitions for Crossroads TCG

import {
  EnvironmentCard,
  CardType,
  EnvironmentSeverity,
  GameState,
  AbilityKeyword,
  ElementType,
  CommanderCard,
  OngoingEffectType,
  OngoingEffectDuration,
  TriggerType,
  TriggerContext,
} from './types';
import {
  createToken,
  healCommander,
  drawCards,
  dealDamage,
  modifyCreaturePower,
  getAllCreatures,
  discardRandomCard,
  returnCreatureToHand,
  flipCoin,
  rollDie,
  returnCardFromGraveyard,
  destroyCreature,
  sacrificeCreature,
  addMana,
  addOngoingEffect,
  getEffectiveCreaturePower,
  addTrigger,
  findCreatureOnBattlefield,
  createBinaryChoice,
  swapCreatureControl,
} from './gameLogic';

// ============================================================================
// THE ENCHANTED FOREST - Environment Deck
// ============================================================================

// UNIVERSAL EFFECTS
export const ENV_OVERGROWN_PATH: EnvironmentCard = {
  id: 'env_forest_overgrown_path',
  name: 'Overgrown Path',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'All creatures get +0/+1. Creatures with power 4+ cannot attack this round.',
  flavorText: 'The forest reclaims what civilization forgot.',
  imageUrl: '',
  effect: (game: GameState) => {
    // All creatures get +0/+1 until end of round
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      addOngoingEffect(game, {
        type: OngoingEffectType.POWER_HEALTH_BUFF,
        duration: OngoingEffectDuration.END_OF_ROUND,
        targetId: creature.instanceId,
        targetType: 'creature',
        createdByPlayerId: creature.ownerId,
        powerModifier: 0,
        healthModifier: 1,
      });
    }

    // Creatures with power 4+ can't attack until end of round
    for (const creature of allCreatures) {
      const effectivePower = getEffectiveCreaturePower(game, creature);
      if (effectivePower >= 4) {
        addOngoingEffect(game, {
          type: OngoingEffectType.CANT_ATTACK,
          duration: OngoingEffectDuration.END_OF_ROUND,
          targetId: creature.instanceId,
          targetType: 'creature',
          createdByPlayerId: creature.ownerId,
        });
      }
    }
  },
};

export const ENV_MOONLIT_GLADE: EnvironmentCard = {
  id: 'env_forest_moonlit_glade',
  name: 'Moonlit Glade',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'First player draws a card. All flying creatures get -1/-0.',
  flavorText: 'Under the canopy, the sky seems distant.',
  imageUrl: '',
  effect: (game: GameState) => {
    // First player draws a card
    drawCards(game, game.firstPlayerIndex, 1);

    // All flying creatures get -1/-0
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      if (creature.keywords.includes(AbilityKeyword.FLYING)) {
        modifyCreaturePower(game, creature.instanceId, -1, 0);
      }
    }
  },
};

export const ENV_ANCIENT_BLESSING: EnvironmentCard = {
  id: 'env_forest_ancient_blessing',
  name: 'Ancient Blessing',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'Each player heals 1 for each creature they control with power 2 or less. When a creature with power 2 or less enters this round, its controller heals 1.',
  flavorText: 'The small and humble find sanctuary here.',
  imageUrl: '',
  effect: (game: GameState) => {
    // Heal for each existing creature with power 2 or less
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      if (creature.power <= 2) {
        healCommander(game, creature.ownerId, 1);
      }
    }

    // Add trigger: when a creature enters, if power 2 or less, heal its controller
    addTrigger(game, {
      type: TriggerType.ON_CREATURE_ENTER,
      duration: OngoingEffectDuration.END_OF_ROUND,
      createdByPlayerId: game.currentPlayerIndex,
      effect: (game: GameState, context: TriggerContext) => {
        if (context.creatureId) {
          const creature = findCreatureOnBattlefield(game, context.creatureId);
          if (creature && creature.power <= 2) {
            healCommander(game, creature.ownerId, 1);
          }
        }
      },
    });
  },
};

export const ENV_WHISPERS_IN_WOODS: EnvironmentCard = {
  id: 'env_forest_whispers',
  name: 'Whispers in the Woods',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'All creatures get -1/-0. Each player may look at the top card of their deck.',
  flavorText: 'The forest shares its secrets with those who listen.',
  imageUrl: '',
  effect: (game: GameState) => {
    // All creatures get -1/-0
    addOngoingEffect(game, {
      type: OngoingEffectType.POWER_HEALTH_BUFF,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'all_creatures',
      createdByPlayerId: game.currentPlayerIndex,
      powerModifier: -1,
      healthModifier: 0,
    });

    // Each player may look at the top card of their deck
    for (const player of game.players) {
      if (player.deck.length > 0) {
        const topCardId = player.deck[0];
        const topCard = game.cardInstances[topCardId];

        if (topCard) {
          createBinaryChoice(
            game,
            player.id,
            'Whispers in the Woods',
            `Top card of your deck: ${topCard.name}`,
            () => {
              // Player chose to acknowledge
            },
            () => {
              // Player chose to skip (same result)
            },
            'OK',
            'OK',
            'env_forest_whispers'
          );
        }
      }
    }
  },
};

export const ENV_THICK_UNDERGROWTH: EnvironmentCard = {
  id: 'env_forest_thick_undergrowth',
  name: 'Thick Undergrowth',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'All creatures cost +1 more. All mana sources produce +1 additional mana.',
  flavorText: 'Progress is slow, but resources are abundant.',
  imageUrl: '',
  effect: (game: GameState) => {
    // All creatures cost +1 more until end of round
    addOngoingEffect(game, {
      type: OngoingEffectType.COST_MODIFIER,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'global',
      createdByPlayerId: game.currentPlayerIndex,
      costModifier: 1,
      filterCardType: CardType.CREATURE,
    });

    // All mana sources produce +1 additional mana until end of round
    addOngoingEffect(game, {
      type: OngoingEffectType.MANA_MODIFIER,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'global',
      createdByPlayerId: game.currentPlayerIndex,
      manaModifier: 1,
    });
  },
};

export const ENV_MUSHROOM_SPORES: EnvironmentCard = {
  id: 'env_forest_mushroom_spores',
  name: 'Mushroom Spores',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  flavorText: 'Death feeds new life in the eternal cycle.',
  description: 'When a creature is defeated, its controller creates a 1/1 Sapling token. (Not yet implemented - no effect)',
  imageUrl: '',
  effect: (game: GameState) => {
    // NOTE: Full implementation requires global defeat trigger system
    // Would need to hook into all creature defeat events
    // Simplified: No effect (requires defeat event listener)
  },
};

export const ENV_CANOPY_COVER: EnvironmentCard = {
  id: 'env_forest_canopy_cover',
  name: 'Canopy Cover',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'All creatures lose Flying. Creatures cannot be targeted by spells.',
  flavorText: 'The leaves hide all beneath.',
  imageUrl: '',
  effect: (game: GameState) => {
    // All creatures lose Flying
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      const flyingIndex = creature.keywords.indexOf(AbilityKeyword.FLYING);
      if (flyingIndex !== -1) {
        creature.keywords.splice(flyingIndex, 1);
      }
    }

    // Since all creatures lost flying, and only flying creatures can be targeted,
    // effectively no creatures can be targeted this round.
    // This is enforced in the UI/targeting system when selecting spell targets.
    // For AI and manual play, players should not target non-flying creatures.
    // A future enhancement could add CANT_BE_TARGETED ongoing effect for formal enforcement.
  },
};

export const ENV_WILD_GROWTH: EnvironmentCard = {
  id: 'env_forest_wild_growth',
  name: 'Wild Growth',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'If the first player controls 3 or more creatures, create a 1/1 Sapling token.',
  flavorText: 'Life begets life in the endless green.',
  imageUrl: '',
  effect: (game: GameState) => {
    const firstPlayer = game.players[game.firstPlayerIndex];
    if (firstPlayer.battlefield.creatures.length >= 3) {
      createToken(game, game.firstPlayerIndex, 'Sapling_1_1', 1, 1);
    }
  },
};

export const ENV_PRIMAL_AWAKENING: EnvironmentCard = {
  id: 'env_forest_primal_awakening',
  name: 'Primal Awakening',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.GAME_CHANGING,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'All creatures gain +2/+2, but attacking creatures deal 1 damage to their controller',
  flavorText: 'Power comes at a price in the ancient woods.',
  imageUrl: '',
  effect: (game: GameState) => {
    // All creatures get +2/+2
    addOngoingEffect(game, {
      type: OngoingEffectType.POWER_HEALTH_BUFF,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'all_creatures',
      createdByPlayerId: game.currentPlayerIndex,
      powerModifier: 2,
      healthModifier: 2,
    });

    // When a creature attacks, deal 1 damage to its controller
    addTrigger(game, {
      type: TriggerType.ON_CREATURE_ATTACK,
      duration: OngoingEffectDuration.END_OF_ROUND,
      createdByPlayerId: game.currentPlayerIndex,
      effect: (game: GameState, context: TriggerContext) => {
        if (context.creatureId) {
          const creature = findCreatureOnBattlefield(game, context.creatureId);
          if (creature) {
            dealDamage(game, String(creature.ownerId), 'player', 1);
          }
        }
      },
    });
  },
};

export const ENV_FOREST_MEMORY: EnvironmentCard = {
  id: 'env_forest_memory',
  name: 'Forest\'s Memory',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.GAME_CHANGING,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'All players may play one creature from their graveyard this round. (Not yet implemented - no effect)',
  flavorText: 'What was lost can be found again among the roots.',
  imageUrl: '',
  effect: (game: GameState) => {
    // NOTE: Full implementation requires modified play-from-graveyard system
    // Would need UI for card selection and special cost handling
    // Simplified: No effect (requires graveyard play mechanic)
  },
};

// FIRST PLAYER TARGETED
export const ENV_TREANT_DEMAND: EnvironmentCard = {
  id: 'env_forest_treant_demand',
  name: 'Treant\'s Demand',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'first_player',
  description: 'First player sacrifices a creature. If they control no creatures, they take 3 damage instead.',
  flavorText: 'The eldest tree demands tribute from those who lead.',
  imageUrl: '',
  effect: (game: GameState) => {
    const firstPlayer = game.players[game.firstPlayerIndex];
    if (firstPlayer.battlefield.creatures.length > 0) {
      // Sacrifice first creature
      const creatureToSac = firstPlayer.battlefield.creatures[0];
      sacrificeCreature(game, creatureToSac.instanceId);
    } else {
      // No creatures, take 3 damage
      dealDamage(game, `player_${game.firstPlayerIndex}`, 'player', 3);
    }
  },
};

export const ENV_TANGLING_ROOTS: EnvironmentCard = {
  id: 'env_forest_tangling_roots',
  name: 'Tangling Roots',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Enchanted Forest',
  targetType: 'first_player',
  description: 'First player cannot use commander abilities this round. All other players heal 1.',
  flavorText: 'The forest tests the bold.',
  imageUrl: '',
  effect: (game: GameState) => {
    // First player cannot use commander abilities this round
    addOngoingEffect(game, {
      type: OngoingEffectType.CANT_USE_ABILITIES,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: game.firstPlayerIndex.toString(),
      targetType: 'player',
      createdByPlayerId: game.currentPlayerIndex,
    });

    // Heal all other players' commanders by 1
    for (let i = 0; i < game.players.length; i++) {
      if (i !== game.firstPlayerIndex) {
        healCommander(game, i, 1);
      }
    }
  },
};

export const ENV_PREDATOR_MARK: EnvironmentCard = {
  id: 'env_forest_predator_mark',
  name: 'Predator\'s Mark',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'first_player',
  description: 'First player\'s creatures get -0/-2. All other players draw a card.',
  flavorText: 'The hunter becomes the hunted.',
  imageUrl: '',
  effect: (game: GameState) => {
    // First player's creatures get -0/-2
    const firstPlayer = game.players[game.firstPlayerIndex];
    for (const creature of firstPlayer.battlefield.creatures) {
      modifyCreaturePower(game, creature.instanceId, 0, -2);
    }

    // Other players draw a card
    for (let i = 0; i < game.players.length; i++) {
      if (i !== game.firstPlayerIndex) {
        drawCards(game, i, 1);
      }
    }
  },
};

export const ENV_UNEXPECTED_AMBUSH: EnvironmentCard = {
  id: 'env_forest_unexpected_ambush',
  name: 'Unexpected Ambush',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.GAME_CHANGING,
  deckTheme: 'Enchanted Forest',
  targetType: 'first_player',
  flavorText: 'Danger lurks for those who venture first.',
  description: 'First player takes 4 damage. All other players\' creatures get +1/+1.',
  imageUrl: '',
  effect: (game: GameState) => {
    // First player takes 4 damage
    dealDamage(game, `player_${game.firstPlayerIndex}`, 'player', 4);

    // Other players' creatures get +1/+1
    for (let i = 0; i < game.players.length; i++) {
      if (i !== game.firstPlayerIndex) {
        const player = game.players[i];
        for (const creature of player.battlefield.creatures) {
          modifyCreaturePower(game, creature.instanceId, 1, 1);
        }
      }
    }
  },
};

export const ENV_MAZE_OF_PATHS: EnvironmentCard = {
  id: 'env_forest_maze',
  name: 'Maze of Paths',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'first_player',
  description: 'First player discards 2 random cards. All other players return their most recently discarded card to hand.',
  flavorText: 'Lost in the woods, you lose what matters most.',
  imageUrl: '',
  effect: (game: GameState) => {
    // First player discards 2
    discardRandomCard(game, game.firstPlayerIndex);
    discardRandomCard(game, game.firstPlayerIndex);

    // Other players return a card from discard
    for (let i = 0; i < game.players.length; i++) {
      if (i !== game.firstPlayerIndex) {
        const player = game.players[i];
        if (player.discard.length > 0) {
          const lastCard = player.discard[player.discard.length - 1];
          returnCardFromGraveyard(game, i, lastCard);
        }
      }
    }
  },
};

// CHAOS/RANDOM EFFECTS
export const ENV_STRANGLING_VINES: EnvironmentCard = {
  id: 'env_forest_strangling_vines',
  name: 'Strangling Vines',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'random',
  description: 'Roll d6: On 1-2, tap a random creature. It cannot untap this round.',
  flavorText: 'The forest reaches out to slow the hasty.',
  imageUrl: '',
  effect: (game: GameState) => {
    const roll = rollDie(6);
    if (roll <= 2) {
      // Tap a random creature and prevent it from untapping until end of round
      const allCreatures = getAllCreatures(game);
      if (allCreatures.length > 0) {
        const randomIndex = Math.floor(Math.random() * allCreatures.length);
        const creature = allCreatures[randomIndex];
        creature.isTapped = true;

        // Add CANT_UNTAP restriction until end of round
        addOngoingEffect(game, {
          type: OngoingEffectType.CANT_UNTAP,
          duration: OngoingEffectDuration.END_OF_ROUND,
          targetId: creature.instanceId,
          targetType: 'creature',
          createdByPlayerId: creature.ownerId,
        });
      }
    }
  },
};

export const ENV_WILL_O_WISP: EnvironmentCard = {
  id: 'env_forest_will_o_wisp',
  name: 'Will-o\'-Wisp',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Enchanted Forest',
  targetType: 'random',
  description: 'Flip coin: On tails, tap a random creature. It cannot attack this round.',
  flavorText: 'Follow the lights... if you dare.',
  imageUrl: '',
  effect: (game: GameState) => {
    const result = flipCoin();
    if (!result) { // tails
      // Tap a random creature and prevent it from attacking until end of round
      const allCreatures = getAllCreatures(game);
      if (allCreatures.length > 0) {
        const randomIndex = Math.floor(Math.random() * allCreatures.length);
        const creature = allCreatures[randomIndex];
        creature.isTapped = true;

        // Add CANT_ATTACK restriction until end of round
        addOngoingEffect(game, {
          type: OngoingEffectType.CANT_ATTACK,
          duration: OngoingEffectDuration.END_OF_ROUND,
          targetId: creature.instanceId,
          targetType: 'creature',
          createdByPlayerId: creature.ownerId,
        });
      }
    }
  },
};

export const ENV_SUDDEN_DOWNPOUR: EnvironmentCard = {
  id: 'env_forest_sudden_downpour',
  name: 'Sudden Downpour',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'random',
  description: 'Random player rolls d6 and loses that much mana from their pool.',
  flavorText: 'The storm comes without warning.',
  imageUrl: '',
  effect: (game: GameState) => {
    const randomPlayerIndex = Math.floor(Math.random() * game.players.length);
    const dieRoll = rollDie(6);
    const player = game.players[randomPlayerIndex];

    // Remove mana from pool (up to available amount)
    let manaToRemove = dieRoll;
    for (const elementType in player.manaPool) {
      if (manaToRemove <= 0) break;
      const available = player.manaPool[elementType as ElementType] || 0;
      const toRemove = Math.min(available, manaToRemove);
      player.manaPool[elementType as ElementType] = (player.manaPool[elementType as ElementType] || 0) - toRemove;
      manaToRemove -= toRemove;
    }
  },
};

export const ENV_FAIRY_MISCHIEF: EnvironmentCard = {
  id: 'env_forest_fairy_mischief',
  name: 'Fairy Mischief',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.CHAOS,
  deckTheme: 'Enchanted Forest',
  targetType: 'random',
  description: 'Random player discards a random card.',
  flavorText: 'The fae delight in harmless pranks... mostly.',
  imageUrl: '',
  effect: (game: GameState) => {
    const randomPlayerIndex = Math.floor(Math.random() * game.players.length);
    discardRandomCard(game, randomPlayerIndex);
  },
};

export const ENV_SHIFTING_SHADOWS: EnvironmentCard = {
  id: 'env_forest_shifting_shadows',
  name: 'Shifting Shadows',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.GAME_CHANGING,
  deckTheme: 'Enchanted Forest',
  targetType: 'random',
  description: 'Roll d6: On 5-6, two random creatures swap controllers until end of round.',
  flavorText: 'In the darkness, nothing is certain.',
  imageUrl: '',
  effect: (game: GameState) => {
    const roll = rollDie(6);
    if (roll >= 5) {
      // Creature control swap on 5-6 roll
      const allCreatures = getAllCreatures(game);
      if (allCreatures.length >= 2) {
        // Swap control of two random creatures until end of round
        const creature1 = allCreatures[Math.floor(Math.random() * allCreatures.length)];
        const remaining = allCreatures.filter(c => c.instanceId !== creature1.instanceId);
        const creature2 = remaining[Math.floor(Math.random() * remaining.length)];

        swapCreatureControl(game, creature1.instanceId, creature2.ownerId, OngoingEffectDuration.END_OF_ROUND);
        swapCreatureControl(game, creature2.instanceId, creature1.ownerId, OngoingEffectDuration.END_OF_ROUND);
      }
    }
  },
};

export const ENV_ENCHANTED_FRUIT: EnvironmentCard = {
  id: 'env_forest_enchanted_fruit',
  name: 'Enchanted Fruit',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Enchanted Forest',
  targetType: 'random',
  description: 'Random player flips coin: Heads = heal 3, Tails = take 2 damage.',
  flavorText: 'Beautiful, tempting, unpredictable.',
  imageUrl: '',
  effect: (game: GameState) => {
    // Random player
    const randomPlayerId = Math.floor(Math.random() * game.players.length);
    const result = flipCoin();

    if (result) { // heads
      healCommander(game, randomPlayerId, 3);
    } else { // tails
      dealDamage(game, `player_${randomPlayerId}`, 'player', 2);
    }
  },
};

export const ENV_MYSTERIOUS_FOG: EnvironmentCard = {
  id: 'env_forest_mysterious_fog',
  name: 'Mysterious Fog',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'Creatures with power 3+ get +1/+0. All creatures deal -1 combat damage this round.',
  flavorText: 'In the mist, only the strong strike true.',
  imageUrl: '',
  effect: (game: GameState) => {
    // Creatures with power 3+ get +1/+0
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      if (creature.power >= 3) {
        addOngoingEffect(game, {
          type: OngoingEffectType.POWER_HEALTH_BUFF,
          duration: OngoingEffectDuration.END_OF_ROUND,
          targetId: creature.instanceId,
          targetType: 'creature',
          createdByPlayerId: game.currentPlayerIndex,
          powerModifier: 1,
          healthModifier: 0,
        });
      }
    }

    // All creatures deal -1 combat damage
    addOngoingEffect(game, {
      type: OngoingEffectType.COMBAT_DAMAGE_MODIFIER,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'all_creatures',
      createdByPlayerId: game.currentPlayerIndex,
      damageModifier: -1,
    });
  },
};

export const ENV_NATURE_LOTTERY: EnvironmentCard = {
  id: 'env_forest_nature_lottery',
  name: 'Nature\'s Lottery',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Enchanted Forest',
  targetType: 'random',
  description: 'Random player rolls a d8. If they roll 8, they draw a card and gain 2 mana of their commander\'s element type.',
  flavorText: 'Fortune favors... someone.',
  imageUrl: '',
  effect: (game: GameState) => {
    const randomPlayerIndex = Math.floor(Math.random() * game.players.length);
    const dieRoll = rollDie(8);

    if (dieRoll === 8) {
      // Lucky! Draw a card (might be a mana source)
      drawCards(game, randomPlayerIndex, 1);
      // And get bonus mana
      const player = game.players[randomPlayerIndex];
      const commanderId = player.commander.cardId;
      const commander = game.cardInstances[commanderId] as CommanderCard;
      if (commander && commander.elementType) {
        addMana(player.manaPool, { [commander.elementType]: 2 });
      }
    }
  },
};

export const ENV_WILD_MAGIC_SURGE: EnvironmentCard = {
  id: 'env_forest_wild_magic_surge',
  name: 'Wild Magic Surge',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.GAME_CHANGING,
  deckTheme: 'Enchanted Forest',
  targetType: 'random',
  description: 'Random player rolls d6: On 1-3 they take 2 damage, on 4-6 they heal 2.',
  flavorText: 'The forest\'s magic answers to no one.',
  imageUrl: '',
  effect: (game: GameState) => {
    const randomPlayerId = Math.floor(Math.random() * game.players.length);
    const roll = rollDie(6);

    if (roll <= 3) {
      // Bad effect: take 2 damage
      dealDamage(game, `player_${randomPlayerId}`, 'player', 2);
    } else {
      // Good effect: heal 2
      healCommander(game, randomPlayerId, 2);
    }
    // Note: Original intended "at end of turn", implementing as immediate
  },
};

export const ENV_THE_HUNT_BEGINS: EnvironmentCard = {
  id: 'env_forest_the_hunt_begins',
  name: 'The Hunt Begins',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.GAME_CHANGING,
  deckTheme: 'Enchanted Forest',
  targetType: 'universal',
  description: 'All creatures get +2/+0, must attack if able, and cannot block until end of round',
  flavorText: 'When the drums sound, all must run.',
  imageUrl: '',
  effect: (game: GameState) => {
    // All creatures get +2/+0 and must attack if able
    addOngoingEffect(game, {
      type: OngoingEffectType.POWER_HEALTH_BUFF,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'all_creatures',
      createdByPlayerId: game.currentPlayerIndex,
      powerModifier: 2,
      healthModifier: 0,
    });

    // All creatures must attack if able
    addOngoingEffect(game, {
      type: OngoingEffectType.MUST_ATTACK,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'all_creatures',
      createdByPlayerId: game.currentPlayerIndex,
    });

    // All creatures cannot block
    addOngoingEffect(game, {
      type: OngoingEffectType.CANT_BLOCK,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'all_creatures',
      createdByPlayerId: game.currentPlayerIndex,
    });
  },
};

// ============================================================================
// ENVIRONMENT DECK REGISTRY
// ============================================================================

export const ENCHANTED_FOREST_DECK = [
  ENV_OVERGROWN_PATH,
  ENV_MOONLIT_GLADE,
  ENV_ANCIENT_BLESSING,
  ENV_WHISPERS_IN_WOODS,
  ENV_THICK_UNDERGROWTH,
  ENV_MUSHROOM_SPORES,
  ENV_CANOPY_COVER,
  ENV_WILD_GROWTH,
  ENV_PRIMAL_AWAKENING,
  ENV_FOREST_MEMORY,
  ENV_TREANT_DEMAND,
  ENV_TANGLING_ROOTS,
  ENV_PREDATOR_MARK,
  ENV_UNEXPECTED_AMBUSH,
  ENV_MAZE_OF_PATHS,
  ENV_STRANGLING_VINES,
  ENV_WILL_O_WISP,
  ENV_SUDDEN_DOWNPOUR,
  ENV_FAIRY_MISCHIEF,
  ENV_SHIFTING_SHADOWS,
  ENV_ENCHANTED_FRUIT,
  ENV_MYSTERIOUS_FOG,
  ENV_NATURE_LOTTERY,
  ENV_WILD_MAGIC_SURGE,
  ENV_THE_HUNT_BEGINS,
];

// ============================================================================
// SCORCHING DESERT - Environment Deck
// ============================================================================

export const ENV_BLAZING_SUN: EnvironmentCard = {
  id: 'env_desert_blazing_sun',
  name: 'Blazing Sun',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Scorching Desert',
  targetType: 'universal',
  description: 'Destroy all creatures with 1 or less health.',
  flavorText: 'The heat is relentless, punishing the weak.',
  imageUrl: '',
  effect: (game: GameState) => {
    // Destroy all creatures with 1 or less toughness
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      if (creature.health <= 1) {
        destroyCreature(game, creature.instanceId);
      }
    }
  },
};

export const ENV_SANDSTORM: EnvironmentCard = {
  id: 'env_desert_sandstorm',
  name: 'Sandstorm',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.CHAOS,
  deckTheme: 'Scorching Desert',
  targetType: 'universal',
  description: 'All creatures get -1/-1 and lose Flying.',
  flavorText: 'Vision is worthless when you cannot see.',
  imageUrl: '',
  effect: (game: GameState) => {
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      // Debuff all creatures
      modifyCreaturePower(game, creature.instanceId, -1, -1);
      // Remove Flying keyword (can't use Flying)
      const flyingIndex = creature.keywords.indexOf(AbilityKeyword.FLYING);
      if (flyingIndex !== -1) {
        creature.keywords.splice(flyingIndex, 1);
      }
    }
  },
};

export const ENV_MIRAGE: EnvironmentCard = {
  id: 'env_desert_mirage',
  name: 'Mirage',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Scorching Desert',
  targetType: 'universal',
  description: 'Each player draws a card, then discards a random card.',
  flavorText: 'Not everything is as it seems.',
  imageUrl: '',
  effect: (game: GameState) => {
    // All players draw a card then discard random
    for (const player of game.players) {
      drawCards(game, player.id, 1);
      discardRandomCard(game, player.id);
    }
  },
};

export const SCORCHING_DESERT_DECK: EnvironmentCard[] = [
  ENV_BLAZING_SUN,
  ENV_SANDSTORM,
  ENV_MIRAGE,
];

// ============================================================================
// FROZEN TUNDRA - Environment Deck
// ============================================================================

export const ENV_BLIZZARD: EnvironmentCard = {
  id: 'env_tundra_blizzard',
  name: 'Blizzard',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.CHAOS,
  deckTheme: 'Frozen Tundra',
  targetType: 'universal',
  description: 'Tap all creatures. They cannot untap until end of round.',
  flavorText: 'The cold bites deeper than any blade.',
  imageUrl: '',
  effect: (game: GameState) => {
    // Tap all creatures and prevent them from untapping until end of round
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      creature.isTapped = true;

      // Add CANT_UNTAP restriction until end of round
      addOngoingEffect(game, {
        type: OngoingEffectType.CANT_UNTAP,
        duration: OngoingEffectDuration.END_OF_ROUND,
        targetId: creature.instanceId,
        targetType: 'creature',
        createdByPlayerId: creature.ownerId,
      });
    }
  },
};

export const ENV_ICE_BARRIER: EnvironmentCard = {
  id: 'env_tundra_ice_barrier',
  name: 'Ice Barrier',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Frozen Tundra',
  targetType: 'universal',
  description: 'All creatures get +0/+2. All creatures played this round enter tapped.',
  flavorText: 'Progress slows to a crawl.',
  imageUrl: '',
  effect: (game: GameState) => {
    // All creatures get +0/+2 until end of round
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      addOngoingEffect(game, {
        type: OngoingEffectType.POWER_HEALTH_BUFF,
        duration: OngoingEffectDuration.END_OF_ROUND,
        targetId: creature.instanceId,
        targetType: 'creature',
        createdByPlayerId: creature.ownerId,
        powerModifier: 0,
        healthModifier: 2,
      });
    }

    // Add global ENTERS_TAPPED effect until end of round
    addOngoingEffect(game, {
      type: OngoingEffectType.ENTERS_TAPPED,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'global',
      createdByPlayerId: game.currentPlayerIndex,
    });
  },
};

export const ENV_FROSTBITE: EnvironmentCard = {
  id: 'env_tundra_frostbite',
  name: 'Frostbite',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Frozen Tundra',
  targetType: 'universal',
  description: 'Deal 1 damage to all creatures with 1 or less health.',
  flavorText: 'The weak perish in the endless winter.',
  imageUrl: '',
  effect: (game: GameState) => {
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      if (creature.health <= 1) {
        dealDamage(game, creature.instanceId, 'creature', 1);
      }
    }
  },
};

export const FROZEN_TUNDRA_DECK: EnvironmentCard[] = [
  ENV_BLIZZARD,
  ENV_ICE_BARRIER,
  ENV_FROSTBITE,
];

// ============================================================================
// STORMY SKIES - Environment Deck
// ============================================================================

export const ENV_LIGHTNING_STORM: EnvironmentCard = {
  id: 'env_sky_lightning_storm',
  name: 'Lightning Storm',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.CHAOS,
  deckTheme: 'Stormy Skies',
  targetType: 'random',
  description: 'Deal 3 damage to a random creature.',
  flavorText: 'The sky strikes without warning.',
  imageUrl: '',
  effect: (game: GameState) => {
    const allCreatures = getAllCreatures(game);
    if (allCreatures.length > 0) {
      const randomIndex = Math.floor(Math.random() * allCreatures.length);
      const target = allCreatures[randomIndex];
      dealDamage(game, target.instanceId, 'creature', 3);
    }
  },
};

export const ENV_TURBULENCE: EnvironmentCard = {
  id: 'env_sky_turbulence',
  name: 'Turbulence',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Stormy Skies',
  targetType: 'universal',
  description: 'All creatures with Flying get -2/-0.',
  flavorText: 'The winds toss about the careless.',
  imageUrl: '',
  effect: (game: GameState) => {
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      if (creature.keywords.includes(AbilityKeyword.FLYING)) {
        modifyCreaturePower(game, creature.instanceId, -2, 0);
      }
    }
  },
};

export const ENV_TAILWIND: EnvironmentCard = {
  id: 'env_sky_tailwind',
  name: 'Tailwind',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Stormy Skies',
  targetType: 'universal',
  description: 'All creatures gain Flying and +1/+0.',
  flavorText: 'The winds favor the bold.',
  imageUrl: '',
  effect: (game: GameState) => {
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      // Grant Flying if they don't have it
      if (!creature.keywords.includes(AbilityKeyword.FLYING)) {
        creature.keywords.push(AbilityKeyword.FLYING);
      }
      // Buff +1/+0
      modifyCreaturePower(game, creature.instanceId, 1, 0);
    }
  },
};

export const STORMY_SKIES_DECK: EnvironmentCard[] = [
  ENV_LIGHTNING_STORM,
  ENV_TURBULENCE,
  ENV_TAILWIND,
];

// ============================================================================
// DEEP OCEAN - Environment Deck
// ============================================================================

export const ENV_TIDAL_WAVE: EnvironmentCard = {
  id: 'env_ocean_tidal_wave',
  name: 'Tidal Wave',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.GAME_CHANGING,
  deckTheme: 'Deep Ocean',
  targetType: 'universal',
  description: 'Return all creatures to their owner\'s hands.',
  flavorText: 'The ocean takes what it will.',
  imageUrl: '',
  effect: (game: GameState) => {
    const allCreatures = getAllCreatures(game);
    // Return all creatures to hand
    for (const creature of allCreatures) {
      returnCreatureToHand(game, creature.instanceId);
    }
  },
};

export const ENV_UNDERTOW: EnvironmentCard = {
  id: 'env_ocean_undertow',
  name: 'Undertow',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Deep Ocean',
  targetType: 'universal',
  description: 'Non-Water creatures cost +1 more mana this round.',
  flavorText: 'The current pulls you under.',
  imageUrl: '',
  effect: (game: GameState) => {
    // Non-Water creatures cost +1 more until end of round
    addOngoingEffect(game, {
      type: OngoingEffectType.COST_MODIFIER,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'global',
      createdByPlayerId: game.currentPlayerIndex,
      costModifier: 1,
      filterCardType: CardType.CREATURE,
      filterElementType: ElementType.WATER,
      invertElementFilter: true, // Apply to all EXCEPT Water creatures
    });
  },
};

export const ENV_CALM_WATERS: EnvironmentCard = {
  id: 'env_ocean_calm_waters',
  name: 'Calm Waters',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Deep Ocean',
  targetType: 'universal',
  description: 'All players draw 1 card and heal 2.',
  flavorText: 'The sea grants a moment of peace.',
  imageUrl: '',
  effect: (game: GameState) => {
    for (const player of game.players) {
      drawCards(game, player.id, 1);
      healCommander(game, player.id, 2);
    }
  },
};

export const DEEP_OCEAN_DECK: EnvironmentCard[] = [
  ENV_TIDAL_WAVE,
  ENV_UNDERTOW,
  ENV_CALM_WATERS,
];

// ============================================================================
// VOLCANIC WASTELAND - Environment Deck
// ============================================================================

export const ENV_ERUPTION: EnvironmentCard = {
  id: 'env_volcanic_eruption',
  name: 'Volcanic Eruption',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.GAME_CHANGING,
  deckTheme: 'Volcanic Wasteland',
  targetType: 'universal',
  description: 'Deal 5 damage to all creatures and all commanders.',
  flavorText: 'The mountain awakens in fury.',
  imageUrl: '',
  effect: (game: GameState) => {
    // Deal 5 damage to all creatures
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      dealDamage(game, creature.instanceId, 'creature', 5);
    }
    // Deal 5 damage to all commanders
    for (const player of game.players) {
      dealDamage(game, `player_${player.id}`, 'player', 5);
    }
  },
};

export const ENV_LAVA_FLOW: EnvironmentCard = {
  id: 'env_volcanic_lava_flow',
  name: 'Lava Flow',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Volcanic Wasteland',
  targetType: 'universal',
  description: 'Deal 2 damage to all non-Fire creatures.',
  flavorText: 'The ground itself becomes hostile.',
  imageUrl: '',
  effect: (game: GameState) => {
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      const card = game.cardInstances[creature.cardId] as any;
      // Deal damage to non-Fire creatures
      if (!card.elementType || card.elementType !== 'fire') {
        dealDamage(game, creature.instanceId, 'creature', 2);
      }
    }
  },
};

export const ENV_GEOTHERMAL_VENT: EnvironmentCard = {
  id: 'env_volcanic_geothermal',
  name: 'Geothermal Vent',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Volcanic Wasteland',
  targetType: 'universal',
  description: 'All Fire mana sources produce +1 additional mana this round.',
  flavorText: 'Power rises from the deep earth.',
  imageUrl: '',
  effect: (game: GameState) => {
    // All Fire mana sources produce +1 additional mana until end of round
    addOngoingEffect(game, {
      type: OngoingEffectType.MANA_MODIFIER,
      duration: OngoingEffectDuration.END_OF_ROUND,
      targetId: 'all',
      targetType: 'global',
      createdByPlayerId: game.currentPlayerIndex,
      manaModifier: 1,
      filterElementType: ElementType.FIRE,
    });
  },
};

export const VOLCANIC_WASTELAND_DECK: EnvironmentCard[] = [
  ENV_ERUPTION,
  ENV_LAVA_FLOW,
  ENV_GEOTHERMAL_VENT,
];

// ============================================================================
// MYSTIC CAVERNS - Environment Deck
// ============================================================================

export const ENV_ECHOING_HALLS: EnvironmentCard = {
  id: 'env_cavern_echoing_halls',
  name: 'Echoing Halls',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Mystic Caverns',
  targetType: 'universal',
  description: 'The first Art spell played this round is copied. (Not yet implemented - no effect)',
  flavorText: 'Every sound repeats endlessly.',
  imageUrl: '',
  effect: (game: GameState) => {
    // NOTE: Full implementation requires Art spell trigger system
    // Would need to hook into Art play events and duplicate effects
    // Simplified: No effect (requires spell-cast event listener)
  },
};

export const ENV_CRYSTAL_FORMATION: EnvironmentCard = {
  id: 'env_cavern_crystal_formation',
  name: 'Crystal Formation',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Mystic Caverns',
  targetType: 'universal',
  description: 'All spells and creatures cost 1 less mana this round. (Not yet implemented - no effect)',
  flavorText: 'The crystals pulse with arcane energy.',
  imageUrl: '',
  effect: (game: GameState) => {
    // NOTE: Full implementation requires ongoing cost reduction system
    // Would need to track -1 cost for all spells and creatures
    // Simplified: No effect (requires persistent cost tracking)
  },
};

export const ENV_UNDERGROUND_RIVER: EnvironmentCard = {
  id: 'env_cavern_underground_river',
  name: 'Underground River',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Mystic Caverns',
  targetType: 'universal',
  description: 'Players may draw up to 2 cards this round instead of 1. (Not yet implemented - no effect)',
  flavorText: 'Hidden waters flow eternal.',
  imageUrl: '',
  effect: (game: GameState) => {
    // NOTE: Full implementation requires turn-based draw modifier
    // Would need to hook into draw phase and allow extra draws
    // Simplified: No effect (requires draw phase modification)
  },
};

export const MYSTIC_CAVERNS_DECK: EnvironmentCard[] = [
  ENV_ECHOING_HALLS,
  ENV_CRYSTAL_FORMATION,
  ENV_UNDERGROUND_RIVER,
];

// ============================================================================
// CELESTIAL PLAINS - Environment Deck
// ============================================================================

export const ENV_DIVINE_RADIANCE: EnvironmentCard = {
  id: 'env_plains_divine_radiance',
  name: 'Divine Radiance',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MODERATE,
  deckTheme: 'Celestial Plains',
  targetType: 'universal',
  description: 'Light creatures get +2/+2. Dark creatures get -2/-2.',
  flavorText: 'The light judges all equally.',
  imageUrl: '',
  effect: (game: GameState) => {
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      const card = game.cardInstances[creature.cardId] as any;
      if (card.elementType === 'light') {
        modifyCreaturePower(game, creature.instanceId, 2, 2);
      } else if (card.elementType === 'dark') {
        modifyCreaturePower(game, creature.instanceId, -2, -2);
      }
    }
  },
};

export const ENV_BLESSED_GROUND: EnvironmentCard = {
  id: 'env_plains_blessed_ground',
  name: 'Blessed Ground',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Celestial Plains',
  targetType: 'universal',
  description: 'All players heal 1.',
  flavorText: 'The sacred land heals all who tread upon it.',
  imageUrl: '',
  effect: (game: GameState) => {
    for (let i = 0; i < game.players.length; i++) {
      healCommander(game, i, 1);
    }
  },
};

export const ENV_ANGELIC_CHOIR: EnvironmentCard = {
  id: 'env_plains_angelic_choir',
  name: 'Angelic Choir',
  type: CardType.ENVIRONMENT,
  severity: EnvironmentSeverity.MILD,
  deckTheme: 'Celestial Plains',
  targetType: 'universal',
  description: 'All creatures with Flying get +0/+2.',
  flavorText: 'The heavens sing of hope.',
  imageUrl: '',
  effect: (game: GameState) => {
    const allCreatures = getAllCreatures(game);
    for (const creature of allCreatures) {
      if (creature.keywords.includes(AbilityKeyword.FLYING)) {
        modifyCreaturePower(game, creature.instanceId, 0, 2);
      }
    }
  },
};

export const CELESTIAL_PLAINS_DECK: EnvironmentCard[] = [
  ENV_DIVINE_RADIANCE,
  ENV_BLESSED_GROUND,
  ENV_ANGELIC_CHOIR,
];

export const ALL_ENVIRONMENT_CARDS: Record<string, EnvironmentCard> = {
  // Enchanted Forest
  [ENV_OVERGROWN_PATH.id]: ENV_OVERGROWN_PATH,
  [ENV_MOONLIT_GLADE.id]: ENV_MOONLIT_GLADE,
  [ENV_ANCIENT_BLESSING.id]: ENV_ANCIENT_BLESSING,
  [ENV_WHISPERS_IN_WOODS.id]: ENV_WHISPERS_IN_WOODS,
  [ENV_THICK_UNDERGROWTH.id]: ENV_THICK_UNDERGROWTH,
  [ENV_MUSHROOM_SPORES.id]: ENV_MUSHROOM_SPORES,
  [ENV_CANOPY_COVER.id]: ENV_CANOPY_COVER,
  [ENV_WILD_GROWTH.id]: ENV_WILD_GROWTH,
  [ENV_PRIMAL_AWAKENING.id]: ENV_PRIMAL_AWAKENING,
  [ENV_FOREST_MEMORY.id]: ENV_FOREST_MEMORY,
  [ENV_TREANT_DEMAND.id]: ENV_TREANT_DEMAND,
  [ENV_TANGLING_ROOTS.id]: ENV_TANGLING_ROOTS,
  [ENV_PREDATOR_MARK.id]: ENV_PREDATOR_MARK,
  [ENV_UNEXPECTED_AMBUSH.id]: ENV_UNEXPECTED_AMBUSH,
  [ENV_MAZE_OF_PATHS.id]: ENV_MAZE_OF_PATHS,
  [ENV_STRANGLING_VINES.id]: ENV_STRANGLING_VINES,
  [ENV_WILL_O_WISP.id]: ENV_WILL_O_WISP,
  [ENV_SUDDEN_DOWNPOUR.id]: ENV_SUDDEN_DOWNPOUR,
  [ENV_FAIRY_MISCHIEF.id]: ENV_FAIRY_MISCHIEF,
  [ENV_SHIFTING_SHADOWS.id]: ENV_SHIFTING_SHADOWS,
  [ENV_ENCHANTED_FRUIT.id]: ENV_ENCHANTED_FRUIT,
  [ENV_MYSTERIOUS_FOG.id]: ENV_MYSTERIOUS_FOG,
  [ENV_NATURE_LOTTERY.id]: ENV_NATURE_LOTTERY,
  [ENV_WILD_MAGIC_SURGE.id]: ENV_WILD_MAGIC_SURGE,
  [ENV_THE_HUNT_BEGINS.id]: ENV_THE_HUNT_BEGINS,

  // Scorching Desert
  [ENV_BLAZING_SUN.id]: ENV_BLAZING_SUN,
  [ENV_SANDSTORM.id]: ENV_SANDSTORM,
  [ENV_MIRAGE.id]: ENV_MIRAGE,

  // Frozen Tundra
  [ENV_BLIZZARD.id]: ENV_BLIZZARD,
  [ENV_ICE_BARRIER.id]: ENV_ICE_BARRIER,
  [ENV_FROSTBITE.id]: ENV_FROSTBITE,

  // Stormy Skies
  [ENV_LIGHTNING_STORM.id]: ENV_LIGHTNING_STORM,
  [ENV_TURBULENCE.id]: ENV_TURBULENCE,
  [ENV_TAILWIND.id]: ENV_TAILWIND,

  // Deep Ocean
  [ENV_TIDAL_WAVE.id]: ENV_TIDAL_WAVE,
  [ENV_UNDERTOW.id]: ENV_UNDERTOW,
  [ENV_CALM_WATERS.id]: ENV_CALM_WATERS,

  // Volcanic Wasteland
  [ENV_ERUPTION.id]: ENV_ERUPTION,
  [ENV_LAVA_FLOW.id]: ENV_LAVA_FLOW,
  [ENV_GEOTHERMAL_VENT.id]: ENV_GEOTHERMAL_VENT,

  // Mystic Caverns
  [ENV_ECHOING_HALLS.id]: ENV_ECHOING_HALLS,
  [ENV_CRYSTAL_FORMATION.id]: ENV_CRYSTAL_FORMATION,
  [ENV_UNDERGROUND_RIVER.id]: ENV_UNDERGROUND_RIVER,

  // Celestial Plains
  [ENV_DIVINE_RADIANCE.id]: ENV_DIVINE_RADIANCE,
  [ENV_BLESSED_GROUND.id]: ENV_BLESSED_GROUND,
  [ENV_ANGELIC_CHOIR.id]: ENV_ANGELIC_CHOIR,
};

export const ENVIRONMENT_DECKS: Record<string, EnvironmentCard[]> = {
  enchanted_forest: ENCHANTED_FOREST_DECK,
  scorching_desert: SCORCHING_DESERT_DECK,
  frozen_tundra: FROZEN_TUNDRA_DECK,
  stormy_skies: STORMY_SKIES_DECK,
  deep_ocean: DEEP_OCEAN_DECK,
  volcanic_wasteland: VOLCANIC_WASTELAND_DECK,
  mystic_caverns: MYSTIC_CAVERNS_DECK,
  celestial_plains: CELESTIAL_PLAINS_DECK,
};
